/**
 * <UpgradeCard>
 *
 * Reusable upgrade prompt rendered when a user lacks the subscription tier
 * required for a feature. Pair with `<FeatureGate feature="…" fallback={<UpgradeCard …/>}>`
 * or render directly from a guarded page.
 *
 * Two modes:
 *  - Tier upgrade: pass `tier` (e.g. "Growth") — CTA routes to Billing.
 *  - Addon activation: pass `addonKey` — CTA routes to Settings > Features.
 */

import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  AddonKey,
  getRequiredTierName,
  useFeatureAccess,
} from "@/hooks/use-feature-access";

export interface UpgradeCardProps {
  /** Display name of the locked feature (e.g. "Spotter AI"). */
  feature: string;
  /** Optional tier name to show ("Growth", "Pro", "Studio"). */
  tier?: string;
  /** Optional long-form description of the locked feature. */
  description?: string;
  /** If the feature is an addon (rather than a tier upgrade), pass the addon key. */
  addonKey?: AddonKey;
  /** Render a compact variant suitable for dashboard widgets. */
  compact?: boolean;
  /** Extra CSS classes for layout tweaks. */
  className?: string;
  /**
   * Optional feature key from `FEATURE_GATES` — if provided, the tier is looked
   * up automatically from the gate definition (overrides `tier`).
   */
  featureKey?: string;
}

export function UpgradeCard({
  feature,
  tier,
  description,
  addonKey,
  compact = false,
  className,
  featureKey,
}: UpgradeCardProps) {
  const navigate = useNavigate();
  const { tier: currentTier } = useFeatureAccess();

  // If featureKey was passed, derive the required tier from FEATURE_GATES.
  const requiredTier = featureKey ? getRequiredTierName(featureKey) ?? tier : tier;

  const isAddon = !!addonKey;
  const ctaLabel = isAddon ? "See add-ons" : "See plans";
  const ctaPath = isAddon ? "/settings?tab=features" : "/settings?tab=billing";

  const heading = isAddon
    ? `Activate ${feature} to unlock this`
    : requiredTier
      ? `Upgrade to ${requiredTier} to unlock ${feature}`
      : `Unlock ${feature}`;

  const subtitle = description ?? (
    isAddon
      ? "Purchase this add-on to enable the feature on your current plan."
      : currentTier
        ? `Your current plan doesn't include ${feature}.`
        : `Choose a plan that includes ${feature}.`
  );

  return (
    <Card
      className={cn(
        "border-dashed bg-muted/30 shadow-none",
        compact ? "max-w-md" : "max-w-xl mx-auto",
        className,
      )}
    >
      <CardHeader className="text-center space-y-3">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          {isAddon ? (
            <Sparkles className="h-5 w-5 text-primary" />
          ) : (
            <Lock className="h-5 w-5 text-primary" />
          )}
        </div>
        <CardTitle className="text-lg font-semibold tracking-tight">
          {heading}
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground max-w-sm mx-auto">
          {subtitle}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex justify-center pb-6">
        <Button
          size={compact ? "sm" : "default"}
          onClick={() => navigate(ctaPath)}
          className="gap-2"
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default UpgradeCard;
